import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import {
  Map,
  BookOpen,
  ChevronRight,
  CheckCircle,
  Clock,
  Users,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { learningPathsAPI, enrollmentsAPI } from '../lib/api';
import { toast } from 'sonner';

export default function LearningPathsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [paths, setPaths] = useState([]);
  const [enrollments, setEnrollments] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pathsRes, enrollmentsRes] = await Promise.all([
        learningPathsAPI.getAll(user?.department),
        enrollmentsAPI.getMy(),
      ]);
      setPaths(pathsRes.data);
      setEnrollments(enrollmentsRes.data);
    } catch (error) {
      toast.error('Failed to load learning paths');
    } finally {
      setLoading(false);
    }
  };

  const getPathProgress = (path) => {
    if (!path.courses || path.courses.length === 0) return 0;
    
    let totalProgress = 0;
    path.courses.forEach((course) => {
      const enrollment = enrollments.find((e) => e.course_id === course.id);
      if (enrollment) {
        totalProgress += enrollment.progress;
      }
    });
    
    return totalProgress / path.courses.length;
  };

  const getEnrolledCount = (path) => {
    if (!path.courses) return 0;
    return path.courses.filter((course) =>
      enrollments.some((e) => e.course_id === course.id)
    ).length;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="learning-paths-page">
        {/* Header */}
        <div>
          <h1 className="font-sketch text-4xl text-ink">Learning Paths</h1>
          <p className="text-muted-foreground mt-1">
            Curated course collections to master specific skills
          </p>
        </div>

        {/* Paths Grid */}
        {paths.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-6">
            {paths.map((path) => {
              const progress = getPathProgress(path);
              const enrolledCount = getEnrolledCount(path);
              const totalCourses = path.courses?.length || 0;

              return (
                <div
                  key={path.id}
                  className="card-sketchy"
                  data-testid={`path-${path.id}`}
                >
                  {/* Header */}
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-14 h-14 bg-primary/10 rounded-lg flex items-center justify-center border-2 border-black">
                      <Map className="w-7 h-7 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-xl text-ink">{path.title}</h3>
                      {path.target_department && (
                        <Badge variant="outline" className="mt-1 text-xs">
                          {path.target_department}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <p className="text-muted-foreground mb-4">{path.description}</p>

                  {/* Progress */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-muted-foreground">
                        {enrolledCount} of {totalCourses} courses enrolled
                      </span>
                      <span className="font-bold text-primary">{Math.round(progress)}%</span>
                    </div>
                    <Progress value={progress} className="h-2" />
                  </div>

                  {/* Courses List */}
                  <div className="space-y-2 mb-4">
                    {path.courses?.slice(0, 3).map((course, index) => {
                      const enrollment = enrollments.find((e) => e.course_id === course.id);
                      const isCompleted = enrollment?.completed_at;

                      return (
                        <Link
                          key={course.id}
                          to={`/courses/${course.id}`}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              isCompleted
                                ? 'bg-green-500 text-white'
                                : enrollment
                                ? 'bg-primary text-white'
                                : 'bg-gray-200 text-gray-600'
                            }`}
                          >
                            {isCompleted ? <CheckCircle className="w-4 h-4" /> : index + 1}
                          </div>
                          <span className="flex-1 text-sm font-medium truncate">
                            {course.title}
                          </span>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </Link>
                      );
                    })}
                    {path.courses?.length > 3 && (
                      <p className="text-sm text-muted-foreground text-center">
                        +{path.courses.length - 3} more courses
                      </p>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 pt-4 border-t border-dashed border-gray-300 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <BookOpen className="w-4 h-4" />
                      <span>{totalCourses} courses</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>
                        {path.courses?.reduce((acc, c) => acc + (c.duration_hours || 0), 0)}h
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="card-sketchy text-center py-16">
            <Map className="w-20 h-20 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-sketch text-2xl text-ink mb-2">No learning paths yet</h3>
            <p className="text-muted-foreground mb-4">
              Check back soon for curated learning paths
            </p>
            <Link to="/courses">
              <Button className="btn-sketchy">Browse Courses</Button>
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
}
